import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  SyntheticEvent
} from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SliderProps } from 'rc-slider';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import { getPersonalBestMoment } from '@shared/awards';
import { DrawnTogetherPhase, MoveType, PromptFormat, SIDE_BET_POINTS, SideBetPrediction, isSideBetMoveType } from '@shared/constants';
import {
  DrawingAssignmentResult,
  GeneratedDrawingResult,
  PlayerResult,
  PromptResult,
  RoomResult,
  UserResult
} from '@shared/api-dto';
import {
  respondToPrompt,
  selectDrawing,
  submitMove,
  updateName,
  uploadDrawing,
  voteDrawing,
  placeSideBet
} from '../../api/endpoints';
import { RadioData, RadioGroup } from './shared/radio-group';
import { Timer } from './shared/timer';
import {
  useTeams,
  getTeamName,
  useTallies,
  useFactOrFibRadios,
  toUnitString,
  quote,
  isGameOver,
  AnswersByUser,
} from './shared/room-data-helpers';
import { TeamList } from './shared/team-list';
import { DrawingCanvas } from './shared/drawing-canvas';
import { getSelectedPosterUrl } from './drawn-together/drawn-together-overlay';

const MAX_FREE_CHARS = 150;

type PlayerRoomProp = {
  user: UserResult;
  roomData: RoomResult;
}

type UserResponses = Record<string, string>;

export const PlayerRoom = ({ roomData, user }: PlayerRoomProp) => {
  const [searchParams] = useSearchParams();
  const [team1, team2] = useTeams(roomData.players);

  const {
    scoresByPlayer,
    prePrompts,
    userAnswersByPrompt
  } = useTallies(roomData);

  const defaultStep = useMemo(() => {
    const responses: Record<string, string> = {};
    roomData.responses.forEach(r => {
      if (r.userGuid === user.guid) {
        responses[r.promptGuid] = r.text;
      }
    });

    let step = 0;
    while (responses[prePrompts[step]?.guid]) {
      ++step;
    }

    if (step === 0) {
      // For name skips, we can start at the first prompt rather than name form.
      return searchParams.get('skip_name') ? 1 : 0;
    }

    // For page reloads, make sure to load the step not yet entered (ie. step + 1).
    return step + 1;
  }, [user.guid, prePrompts, roomData.responses]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepNumber, setStepNumber] = useState<number>(defaultStep);
  const prePromptKey = prePrompts.map(p => p.guid).join(',');

  // Host can add/remove round types in the lobby; jump to the next incomplete
  // prompt (or finished) without resetting the name step.
  useEffect(() => {
    setStepNumber(prev => {
      if (prev === 0) {
        return 0;
      }
      const responses: Record<string, string> = {};
      roomData.responses.forEach(r => {
        if (r.userGuid === user.guid) {
          responses[r.promptGuid] = r.text;
        }
      });
      let step = 0;
      while (responses[prePrompts[step]?.guid]) {
        ++step;
      }
      return step + 1;
    });
    // Only when the prompt set itself changes, not when the player answers.
  }, [prePromptKey, user.guid]);

  const player = useMemo(() => (
    roomData.players.find(p => p.user.guid === user.guid)
  ), [roomData.players]);

  const curPrompt = prePrompts[stepNumber - 1];
  const isMultiChoice = Boolean(curPrompt?.type === PromptFormat.MULTIPLE_CHOICE);
  const userResponses: UserResponses = useMemo(() => {
    const responses: UserResponses = {};
    roomData.responses.forEach(r => {
      if (r.userGuid === user.guid) {
        responses[r.promptGuid] = r.text;
      }
    });
    return responses;
  }, [user.guid, roomData.responses]);

  const getLatestPlayerName = () => {
    let name = user.name;
    if (player?.user.name) {
      name = player.user.name;
    }
    return name;
  };

  const onNameComplete = useCallback(() => {
    setStepNumber(1);
  }, []);

  const onChoose = async (text?: string) => {
    setIsLoading(true);
    try {
      setErrorMessage(null);
      text = text?.trim();
      if (!text) {
        throw new Error('Please provide a response');
      }
      await respondToPrompt({
        code: roomData.code,
        guid: curPrompt.guid,
        text
      });
      setStepNumber(stepNumber + 1);
      setIsLoading(false);
    } catch (e: any) {
      setErrorMessage(e.message);
      setIsLoading(false);
    }
  };

  const onBack = () => {
    setStepNumber(stepNumber - 1);
  };

  const getSetupContent = () => {
    if (stepNumber === 0) {
      return <NameForm
        code={roomData.code}
        defaultName={getLatestPlayerName()}
        onComplete={onNameComplete}
      />;
    }

    if (!curPrompt || stepNumber > prePrompts.length) {
      if (roomData.isActive && player?.team) {
        if (isGameOver(roomData)) {
          return (
            <GameRecap
              roomData={roomData}
              user={user}
              player={player}
              score={scoresByPlayer[user.guid] ?? 0}
            />
          );
        }
        return (
          <>
            <div className='moveTop'>
              <span className='chevrons statusPill'>{`Team ${getTeamName(player.team)}`}</span>
            </div>
            <p className='moveKicker center'>Your team</p>
            <TeamList team={player.team === 1 ? team1 : team2} scores={scoresByPlayer} teamNumber={player.team} />
            <p className='moveFooter'>Keep an eye on the big screen</p>
          </>
        );
      } else {
        return (
          <div className='waitingRoom'>
            <div className='moveTop'>
              <span className='chevrons statusPill'>You&apos;re in!</span>
            </div>
            <h2 className='moveQuestion'>Waiting for the host to start the game</h2>
            <p className='moveFooter'>Look up at the big screen</p>
            <button className='back' onClick={onBack}>Back</button>
          </div>
        );
      }
    }

    return (
      <>
        <p className='moveKicker'>{`Step ${stepNumber} of ${prePrompts.length}`}</p>
        {errorMessage && (<p className='red'>{'' + errorMessage}</p>)}
        <h2 className='moveQuestion'>{curPrompt.text}</h2>
        {isMultiChoice
          ? (
            <ChoiceForm
              key={curPrompt.text}
              isLoading={isLoading}
              responses={userResponses}
              prompt={curPrompt}
              onBack={onBack}
              onNext={onChoose} />
            )
          : (
            <FreeTextForm
              key={curPrompt.text}
              isLoading={isLoading}
              responses={userResponses}
              prompt={curPrompt}
              onBack={onBack}
              onNext={onChoose} />
            )}
      </>
    );
  };

  const getMoveContent = () => {
    const move = roomData.currentMove;
    if (!move) {
      console.error('rendering move content without move', roomData);
      return null;
    }

    if (move.type === MoveType.SLIDER_ALL_PLAY) {
      return (
        <SliderForm roomData={roomData} player={player} />
      );
    }

    if (move.type === MoveType.DRAWN_TOGETHER) {
      return (
        <DrawingForm roomData={roomData} user={user} />
      );
    }

    if (user.guid !== move.player?.user.guid) {
      if (isSideBetMoveType(move.type)) {
        return (
          <SideBetForm roomData={roomData} user={user} player={player} />
        );
      }
      const isTeammate = move.player?.team === player?.team;
      return (
        <div className='waitingRoom'>
          <div className='moveTop'>
            <span className='chevrons statusPill'>{`${move.player?.user.name}'s turn`}</span>
          </div>
          <h2 className='moveQuestion'>
            {isTeammate ? 'Help your teammate decide!' : 'Sit tight'}
          </h2>
          <p className='moveFooter'>
            {isTeammate
              ? `${move.player?.user.name} locks in the answer on their phone`
              : 'Watch the big screen'}
          </p>
        </div>
      );
    }

    if (roomData.currentMove?.submitted) {
      return (
        <div className='waitingRoom'>
          <div className='moveTop'>
            <span className='chevrons statusPill'>Locked in!</span>
          </div>
          <h2 className='moveQuestion'>Now check the big screen to see if you were right</h2>
        </div>
      );
    }

    return (
      <MoveForm
        roomData={roomData}
        player={player}
        answersByUser={userAnswersByPrompt[move.prompt.guid]}
      />
    );
  };

  const gameIsStarted = roomData.isActive && roomData.currentMove;
  // Drawing needs as much screen as possible, so the room gets a compact layout.
  const isDrawing = gameIsStarted && roomData.currentMove?.type === MoveType.DRAWN_TOGETHER;

  const classNames = [
    'playerRoom',
    roomData.isActive ? 'team' + player?.team : '',
    isDrawing ? 'drawing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      {gameIsStarted ? getMoveContent() : getSetupContent()}
    </div>
  );
};

type GameRecapProps = {
  roomData: RoomResult;
  user: UserResult;
  player: PlayerResult;
  score: number;
};

const GameRecap = ({ roomData, user, player, score }: GameRecapProps) => {
  const team = player.team;
  if (!team) {
    return null;
  }

  const moment = getPersonalBestMoment(roomData, user.guid);
  const drawMove = roomData.moves?.find(m => m.type === MoveType.DRAWN_TOGETHER);
  const assignment = drawMove?.drawingAssignments?.find(da => da.drawer.user.guid === user.guid);
  const posterUrl = assignment ? getSelectedPosterUrl(assignment) : undefined;
  const pts = score === 1 ? 'point' : 'points';

  return (
    <div className='gameRecap'>
      <div className='moveTop'>
        <span className='chevrons statusPill'>
          {`Game over \u00b7 Team ${getTeamName(team)}`}
        </span>
      </div>
      <p className='moveKicker'>Your recap</p>
      <p className='recapScore'>{`${score} ${pts}`}</p>
      <p className='recapMoment'>{moment}</p>
      {posterUrl && (
        <img className='recapPoster' src={posterUrl} alt='Your Drawn Together poster' />
      )}
      <p className='moveFooter'>Watch the big screen</p>
    </div>
  );
};

type SliderFormProps = {
  roomData: RoomResult;
  player?: PlayerResult;
}

const SliderForm = ({ player, roomData }: SliderFormProps) => {
  const answer = roomData.currentMove?.prompt.answer;
  const [err, setErr] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [value, setValue] = useState<number>(answer?.default ?? 0);

  const response = useMemo(() => (
    roomData.responses.find(r => (
      r.promptGuid === roomData.currentMove?.prompt.guid &&
        r.userGuid === player?.user.guid
    ))
  ), [player, roomData.currentMove, roomData.responses]);

  const handleChange: SliderProps['onChange'] = (newValue) => {
    setValue(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  const onSubmit = async () => {
    try {
      setErr(null);
      setIsLoading(true);
      await respondToPrompt({
        code: roomData.code,
        guid: roomData.currentMove!.prompt.guid,
        text: value.toString()
      });
      setIsLoading(false);
      setIsSubmitted(true);
    } catch (e) {
      console.error('could not save response', e);
      setIsLoading(false);
      setIsSubmitted(false);
      setErr('Could not save response');
    }
  };

  if (response || isSubmitted) {
    return (
      <div className='waitingRoom'>
        <div className='moveTop'>
          <span className='chevrons statusPill'>Locked in!</span>
        </div>
        <h2 className='moveQuestion'>Thanks! Now watch the big screen</h2>
      </div>
    );
  }

  if (!answer) {
    return <p>Could not find answer data.</p>;
  }

  const teamName = player?.team ? ` \u00b7 Team ${getTeamName(player.team)}` : '';

  return (
    <div className='movePanel'>
      <div className='moveTop'>
        <span className='chevrons statusPill'>{`Everyone plays${teamName}`}</span>
        <Timer key={'timer_all_play'}
          initialSeconds={(roomData?.timeLeft ?? 0) / 1000}
          onComplete={() => console.log('all done!')} />
      </div>
      {err && <p className='red'>{err}</p>}
      <p className='moveKicker'>Take your best guess</p>
      <h2 className='moveQuestion'>{roomData.currentMove?.prompt.text}</h2>
      <div className='sliderCard'>
        <div className='sliderValue'>{toUnitString(value, answer.units)}</div>
        <Slider
          min={answer.min}
          max={answer.max}
          value={value}
          onChange={handleChange}
        />
      </div>
      <button className='block' disabled={isLoading} onClick={onSubmit}>
        Lock in guess
      </button>
      <p className='moveFooter'>Your team&apos;s average is what counts</p>
    </div>
  );
};

type MoveFormProps = {
  roomData: RoomResult;
  player?: PlayerResult;
  answersByUser: AnswersByUser;
};

const MoveForm = ({ player, roomData, answersByUser }: MoveFormProps) => {
  if (!roomData.currentMove) {
    console.error('cannot render move form without move', roomData);
    return null;
  }

  const code = roomData.code;
  const move = roomData.currentMove;
  const { author } = roomData.currentMove?.factOrFib ?? {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [err, setErr] = useState<string | undefined>(undefined);
  const [choice, setChoice] = useState<string | null>(null);

  const FoFRadios = useFactOrFibRadios(roomData, answersByUser, choice);

  const radios = useMemo<RadioData[]>(() => {
    return (move.prompt.choices?.map(c => ({
      text: c,
      value: c,
      checked: choice === c
    })) ?? []);
  }, [move.prompt.choices, choice]);

  const onSelect = async (c: string) => {
    const prevChoice = choice;
    try {
      setErr(undefined);
      setIsLoading(true);
      setChoice(c);
      /*
      await setMove({
        code,
        promptGuid: move.prompt.guid,
        text: c
      });
      */
      setIsLoading(false);
    } catch (e) {
      console.error('could not set choice', e);
      setIsLoading(false);
      setChoice(prevChoice);
      setErr('Failed to update choice on the server, please try again');
    }
  };

  const submitChoice = async () => {
    try {
      setErr(undefined);
      setIsLoading(true);
      await submitMove({
        code,
        promptGuid: move.prompt.guid,
        text: choice ?? undefined
      });
      setIsLoading(false);
      setIsSubmitted(true);
    } catch (e) {
      console.error('could not submit choice', e);
      setIsLoading(false);
      setIsSubmitted(false);
      setErr('Failed to submit choice to the server, please try again');
    }
  };

  const isFoFib = move.type === MoveType.FREE_FACT_OR_FIB;
  const teamName = player?.team ? ` \u00b7 Team ${getTeamName(player.team)}` : '';

  return (
    <div className='movePanel'>
      <div className='moveTop'>
        <span className='chevrons statusPill'>{`Your turn${teamName}`}</span>
        <Timer
          key={'timer' + move.player?.user.guid}
          initialSeconds={(roomData?.timeLeft ?? 0) / 1000}
          onComplete={() => console.log('all done!')}
        />
      </div>
      {err && <p className='red'>{err}</p>}
      <p className='moveKicker'>
        {isFoFib
          ? `What did ${author?.user.name} say when asked:`
          : 'Pick the most popular'}
      </p>
      <h2 className='moveQuestion'>
        {isFoFib ? quote(roomData.currentMove?.prompt.text) : roomData.currentMove?.prompt.text}
      </h2>
      <RadioGroup
        disabled={isLoading || isSubmitted}
        onSelect={onSelect}
        name='move'
        radios={isFoFib ? FoFRadios : radios} />
      <button
        className='block'
        disabled={isSubmitted || isLoading || !choice}
        onClick={submitChoice}>
        Lock in answer
      </button>
      <p className='moveFooter'>You can change your pick until you lock it in.</p>
    </div>
  );
};

type SideBetFormProps = {
  roomData: RoomResult;
  user: UserResult;
  player?: PlayerResult;
};

const SIDE_BET_LABEL: Record<SideBetPrediction, string> = {
  [SideBetPrediction.CORRECT]: 'Nails it',
  [SideBetPrediction.WRONG]: 'Whiffs',
};

const SideBetForm = ({ roomData, user, player }: SideBetFormProps) => {
  const move = roomData.currentMove!;
  const serverBet = move.sideBets?.find(bet => bet.bettorGuid === user.guid)?.prediction;
  const [choice, setChoice] = useState<SideBetPrediction | undefined>(serverBet);
  const [localBet, setLocalBet] = useState<SideBetPrediction | undefined>(serverBet);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitted = move.submitted;
  const isTeammate = move.player?.team === player?.team;
  const lockedBet = serverBet ?? localBet;
  const playerName = move.player?.user.name ?? 'they';
  const authorName = move.factOrFib?.author?.user.name;
  const isFoFib = move.type === MoveType.FREE_FACT_OR_FIB;
  const kicker = isFoFib
    ? `${playerName} is trying to guess what ${authorName ?? 'they'} said`
    : `${playerName} is trying to pick the most popular answer`;
  const nailsCaption = isFoFib
    ? `Guesses ${authorName ? `${authorName}'s` : 'their'} answer`
    : 'Picks the most popular answer';
  const whiffsCaption = isFoFib
    ? `Misses ${authorName ? `${authorName}'s` : 'their'} answer`
    : 'Misses the most popular answer';

  const onPick = async (prediction: SideBetPrediction) => {
    if (submitted || isSubmitting) {
      return;
    }
    const prev = choice;
    const clearing = choice === prediction;
    const next = clearing ? undefined : prediction;
    setChoice(next);
    setError(null);
    setIsSubmitting(true);
    try {
      await placeSideBet({
        code: roomData.code,
        promptGuid: move.prompt.guid,
        prediction: clearing ? null : prediction,
      });
      setLocalBet(next);
    } catch (e) {
      console.error('could not place side bet', e);
      setChoice(prev);
      setError(clearing
        ? 'Failed to clear your bet. Please try again.'
        : 'Failed to place your bet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className='waitingRoom'>
        <div className='moveTop'>
          <span className='chevrons statusPill'>{`${move.player?.user.name}'s turn`}</span>
        </div>
        <h2 className='moveQuestion'>
          {lockedBet
            ? `You bet ${SIDE_BET_LABEL[lockedBet]}. Watch the big screen.`
            : 'You sat this one out. Watch the big screen.'}
        </h2>
      </div>
    );
  }

  return (
    <div className='movePanel'>
      <div className='moveTop'>
        <span className='chevrons statusPill'>{`${move.player?.user.name}'s turn`}</span>
        <Timer
          key={'timer_side_bet' + move.prompt.guid}
          initialSeconds={(roomData?.timeLeft ?? 0) / 1000}
          onComplete={() => console.log('side bets closed')}
        />
      </div>
      {error && <p className='red'>{error}</p>}
      <p className='moveKicker'>{kicker}</p>
      <h2 className='moveQuestion'>
        {isFoFib
          ? quote(move.prompt.text)
          : move.prompt.text}
      </h2>
      <div className='sideBetChoices'>
        <button
          type='button'
          className={choice === SideBetPrediction.CORRECT ? 'chosen' : 'reverse'}
          disabled={isSubmitting}
          onClick={() => onPick(SideBetPrediction.CORRECT)}
        >
          Nails it
          <span className='sideBetCaption'>{nailsCaption}</span>
        </button>
        <button
          type='button'
          className={choice === SideBetPrediction.WRONG ? 'chosen' : 'reverse'}
          disabled={isSubmitting}
          onClick={() => onPick(SideBetPrediction.WRONG)}
        >
          Whiffs
          <span className='sideBetCaption'>{whiffsCaption}</span>
        </button>
      </div>
      <p className='moveFooter sideBetStakes'>
        {`Optional \u00b7 +${SIDE_BET_POINTS} if you're right, \u2212${SIDE_BET_POINTS} if you're wrong`}
      </p>
      <p className='sideBetHint'>
        {isTeammate
          ? `Help ${move.player?.user.name} decide \u00b7 Change until they lock in`
          : "Don't help \u00b7 Change until they lock in"}
      </p>
    </div>
  );
};

type PromptFormProps = {
  isLoading: boolean;
  responses: UserResponses;
  prompt: PromptResult;
  onBack: () => void;
  onNext: (choice?: string) => void;
}

const FreeTextForm = ({ isLoading, responses, prompt, onBack, onNext }: PromptFormProps) => {
  const [value, setValue] = useState<string>(responses[prompt.guid] ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const onKeyDown = (evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      onSubmit();
    }
  };

  const onChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(evt.target.value);
  };

  const onSubmit = useCallback(() => {
    onNext(textareaRef.current?.value);
  }, [textareaRef]);

  const left = MAX_FREE_CHARS - value.length;

  return (
    <>
      {prompt.type === PromptFormat.FREE_INPUT && (
        <p className='formHint'>
          Explain why you chose your answer, but don&apos;t include anything that could identify you.
        </p>
      )}
      {prompt.type === PromptFormat.DRAWING && (
        <p className='formHint'>
          Someone else will draw this, so pick a moment they can picture.
        </p>
      )}
      <textarea
        maxLength={MAX_FREE_CHARS}
        ref={textareaRef}
        autoFocus
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={value}
      ></textarea>
      <div className={`charCount${left <= 10 ? ' red' : ''}`}>
        <span>{left}</span> characters left
      </div>
      <div className='formActions'>
        <button className='back' onClick={onBack}>Back</button>
        <button disabled={isLoading} onClick={onSubmit}>Next</button>
      </div>
    </>
  );
};
const ChoiceForm = ({ isLoading, responses, prompt, onBack, onNext }: PromptFormProps) => {
  const [choice, setChoice] = useState<string | undefined>(responses[prompt?.guid]);
  const radios = useMemo(() => {
    return prompt.choices?.map(c => ({
      text: c,
      checked: choice === c
    })) ?? [];
  }, [prompt.choices, choice]);

  const onSubmit = useCallback(() => {
    onNext(choice);
  }, [choice]);

  return (
    <>
      <RadioGroup disabled={isLoading} onSelect={setChoice} radios={radios} name='prePrompt' />
      <div className='formActions'>
        <button className='back' onClick={onBack}>Back</button>
        <button className='next' disabled={isLoading || !choice} onClick={onSubmit}>Next</button>
      </div>
    </>
  );
};

type NameFormProps = {
  code: string; defaultName: string;
  onComplete: () => void;
}

const NameForm = ({ code, defaultName, onComplete }: NameFormProps) => {
  const nameRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!nameRef.current) {
      console.error('submit too fast');
      return;
    }

    const newName = nameRef.current.value;
    if (newName && newName !== defaultName) {
      try {
        await updateName(code, newName);
      } catch (e: any) {
        console.error('couldnt update name', e);
      }
    }

    onComplete();
  };

  return (
    <form className='nameForm' onSubmit={onSubmit}>
      <div className='moveTop'>
        <span className='chevrons statusPill'>Welcome!</span>
      </div>
      <p className='moveKicker'>First things first</p>
      <h2 className='moveQuestion'>What should we call you?</h2>
      <input autoFocus ref={nameRef} maxLength={14} type="text" placeholder={defaultName} />
      <button type='submit' className='next block'>Let&apos;s go</button>
    </form>
  );
};

type DrawingFormProps = {
  roomData: RoomResult;
  user: UserResult;
}

const DrawingForm = ({ roomData, user }: DrawingFormProps) => {
  const move = roomData.currentMove!;
  const assignment = move.drawingAssignments?.find(da => da.drawer.user.guid === user.guid);
  const [isSaving, setIsSaving] = useState(false);
  const [isRedrawing, setIsRedrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const posters = assignment?.generatedDrawings ?? [];
  const showCanvas = isRedrawing || posters.length === 0;
  const phase = move.phase ?? DrawnTogetherPhase.DRAWING;

  if (move.submitted) {
    return <h3>The votes are in! Watch the big screen.</h3>;
  }

  if (phase === DrawnTogetherPhase.PRESENTING) {
    return <h3>Posters are locked in. Watch the big screen!</h3>;
  }

  if (phase === DrawnTogetherPhase.VOTING) {
    return <VoteForm roomData={roomData} user={user} />;
  }

  const handleDrawingSave = async (dataUrl: string) => {
    if (!assignment) return;

    setIsSaving(true);
    setError(null);

    try {
      // The upload also generates the posters, so this takes a while.
      await uploadDrawing({
        roomCode: roomData.code,
        userGuid: user.guid,
      }, dataUrl);
      setIsRedrawing(false);
    } catch (err) {
      console.error('Error saving drawing', err);
      setError('Failed to save drawing. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!assignment) {
    return <p>Waiting for your drawing assignment...</p>;
  }

  if (!showCanvas) {
    return (
      <PosterPicker
        roomCode={roomData.code}
        anecdote={assignment.responseToDraw.text}
        posters={posters}
        onRedraw={() => setIsRedrawing(true)}
      />
    );
  }

  return (
    <>
      <p className="drawingInstructions">
        <b>Draw:</b> {quote(assignment.responseToDraw.text)}
      </p>
      {!isSaving && assignment.drawing && posters.length === 0 && (
        <p className="drawingStatus red">No posters came back. Try saving again.</p>
      )}
      {error && <p className="drawingStatus red">{error}</p>}
      <DrawingCanvas
        onSave={handleDrawingSave}
        initialDrawing={assignment.drawing ? `/uploads/${assignment.drawing}` : undefined}
        isSaving={isSaving}
        savingMessage="Turning your drawing into movie posters... this takes about a minute."
      />
    </>
  );
};

type PosterPickerProps = {
  roomCode: string;
  anecdote: string;
  posters: GeneratedDrawingResult[];
  onRedraw: () => void;
}

/**
 * After the posters are generated, the drawer picks the one that best
 * represents their drawing to show on the big screen.
 */
const PosterPicker = ({ roomCode, anecdote, posters, onRedraw }: PosterPickerProps) => {
  const serverSubmitted = posters.find(p => p.selected)?.service;
  const [choice, setChoice] = useState<string | undefined>(serverSubmitted);
  // Remembered locally so the UI reflects the submission before the room update arrives.
  const [localSubmitted, setLocalSubmitted] = useState<string | undefined>(serverSubmitted);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the drawer reopens the gallery to swap their already-chosen poster.
  const [isRevising, setIsRevising] = useState(false);

  const submitted = serverSubmitted ?? localSubmitted;
  const isCurrentChoiceSubmitted = Boolean(choice) && choice === submitted;
  const submittedPoster = posters.find(poster => poster.service === submitted);

  const onSubmit = async () => {
    if (!choice) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await selectDrawing({ code: roomCode, service: choice });
      setLocalSubmitted(choice);
      setIsRevising(false);
    } catch (err) {
      console.error('Error selecting poster', err);
      setError('Failed to submit your choice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Once a poster is locked in, show only that one while the round catches up.
  if (submittedPoster && !isRevising) {
    return (
      <>
        <p className="drawingInstructions">
          <b>Thanks!</b> Your poster is in. Watch the big screen while everyone else finishes.
        </p>
        <figure className="chosenPoster">
          <img src={submittedPoster.url} alt={submittedPoster.description} />
          <figcaption>{submittedPoster.description}</figcaption>
        </figure>
        <div className="drawing-controls">
          <button className="reverse" onClick={onRedraw}>
            Redraw
          </button>
          <button className="reverse" onClick={() => setIsRevising(true)}>
            Change poster
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="drawingInstructions">
        <b>Pick your poster</b> for {quote(anecdote)}
      </p>
      {error && <p className="drawingStatus red">{error}</p>}
      <div className="posterPicker">
        {posters.map(poster => (
          <div
            key={poster.service}
            role="button"
            className={`poster${choice === poster.service ? ' chosen' : ''}`}
            onClick={() => setChoice(poster.service)}
          >
            <img src={poster.url} alt={poster.description} />
            <span className="posterLabel">{poster.description}</span>
          </div>
        ))}
      </div>
      {isCurrentChoiceSubmitted && (
        <p className="drawingStatus">This is the poster you already submitted.</p>
      )}
      <div className="drawing-controls">
        <button className="reverse" disabled={isSubmitting} onClick={onRedraw}>
          Redraw
        </button>
        {isCurrentChoiceSubmitted
          ? (
          <button onClick={() => setIsRevising(false)}>
            Done
          </button>
            )
          : (
          <button disabled={isSubmitting || !choice} onClick={onSubmit}>
            {isSubmitting ? 'Submitting...' : (submitted ? 'Change choice' : 'Submit')}
          </button>
            )}
      </div>
    </>
  );
};

type VoteFormProps = {
  roomData: RoomResult;
  user: UserResult;
}

/**
 * Voting phase: pick a favorite among everyone else's posters.
 */
const VoteForm = ({ roomData, user }: VoteFormProps) => {
  const move = roomData.currentMove!;
  const candidates = (move.drawingAssignments ?? []).filter(da => (
    da.drawer.user.guid !== user.guid && da.generatedDrawings?.some(gd => gd.selected)
  ));

  const serverVote = candidates.find(da => da.voterGuids.includes(user.guid))?.drawer.user.guid;
  const [choice, setChoice] = useState<string | undefined>(serverVote);
  const [localVote, setLocalVote] = useState<string | undefined>(serverVote);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const voted = serverVote ?? localVote;
  const isCurrentChoiceVoted = Boolean(choice) && choice === voted;

  const onSubmit = async () => {
    if (!choice) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await voteDrawing({ code: roomData.code, drawerGuid: choice });
      setLocalVote(choice);
    } catch (err) {
      console.error('Error voting for poster', err);
      setError('Failed to submit your vote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const posterFor = (assignment: DrawingAssignmentResult) => (
    assignment.generatedDrawings?.find(gd => gd.selected)
  );

  if (candidates.length === 0) {
    return <h3>No posters to vote on. Watch the big screen!</h3>;
  }

  return (
    <>
      <Timer key={'timer_vote' + move.prompt.guid}
        initialSeconds={(roomData?.timeLeft ?? 0) / 1000}
        onComplete={() => console.log('voting closed')} />
      <p className="drawingInstructions">
        <b>Vote</b> for your favorite poster
      </p>
      {error && <p className="drawingStatus red">{error}</p>}
      <div className="posterPicker">
        {candidates.map(assignment => (
          <div
            key={assignment.drawer.user.guid}
            role="button"
            className={`poster${choice === assignment.drawer.user.guid ? ' chosen' : ''}`}
            onClick={() => setChoice(assignment.drawer.user.guid)}
          >
            <img src={posterFor(assignment)?.url} alt={`Poster drawn by ${assignment.drawer.user.name}`} />
            <span className="posterLabel">Drawn by {assignment.drawer.user.name}</span>
          </div>
        ))}
      </div>
      {isCurrentChoiceVoted && (
        <p className="drawingStatus">Vote submitted! Now watch the big screen.</p>
      )}
      <div className="drawing-controls">
        <button
          disabled={isSubmitting || !choice || isCurrentChoiceVoted}
          onClick={onSubmit}
        >
          {isSubmitting ? 'Voting...' : (voted ? 'Change vote' : 'Vote')}
        </button>
      </div>
    </>
  );
};
